import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Swal from 'sweetalert2';
import { useDispatch } from 'react-redux';
import { setDoctor } from '../../features/doctor/doctorSlice';
import config from '../../config';
import Header from '../../components/common/Header';
import Footer from '../../components/common/Footer';

// Doctor portal login — deliberately separate from the patient/member Login.jsx,
// since doctor accounts are provisioned by an admin (see backend/scripts/createDoctor.js)
// rather than through public sign-up. No Header/Footer here since this isn't a
// public marketing page — swap in your own internal layout if you have one.
const DoctorLogin = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [formData, setFormData] = useState({ email: '', password: '' });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.email) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Email is invalid';
    }
    if (!formData.password) {
      newErrors.password = 'Password is required';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setIsSubmitting(true);
    try {
      const response = await axios.post(`${config.baseURL}/api/doctor-auth/login`, {
        email: formData.email,
        password: formData.password,
      });

      if (response.data.success) {
        dispatch(setDoctor({
          doctorInfo: response.data.doctor,
          token: response.data.token,
        }));

        // Same pattern as the patient login — persist separately from the
        // patient token so a doctor and patient session can't collide if
        // someone has both open in the same browser.
        localStorage.setItem('doctor_token', response.data.token);

        await Swal.fire({
          title: 'Success',
          text: 'Logged in successfully!',
          icon: 'success',
          showConfirmButton: false,
          timer: 2000,
        });

        navigate('/doctor/dashboard');
      }
    } catch (error) {
      if (error.response?.status === 403) {
        await Swal.fire({
          title: 'Account Inactive',
          text: error.response.data.message || 'This doctor account is inactive. Contact admin.',
          icon: 'warning',
          showConfirmButton: false,
          timer: 2500,
        });
      } else if (error.response?.status === 401) {
        await Swal.fire({
          title: 'Unauthorized',
          text: 'Invalid email or password.',
          icon: 'error',
          showConfirmButton: false,
          timer: 2000,
        });
      } else {
        await Swal.fire({
          title: 'Login Failed',
          text: 'Something went wrong. Please try again.',
          icon: 'error',
          showConfirmButton: false,
          timer: 2000,
        });
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const togglePasswordVisibility = () => setShowPassword(!showPassword);

  return (
    <>
    <Header/>
    <section className="login-part doctor-login-part">
      <div className="container">
        <div className="row" style={{ justifyContent: 'center' }}>
          <div className="col-md-6">
            <div className="login-box">
              <div className="login-title">Doctor Sign In</div>
              <p style={{ color: '#888', marginBottom: 20, fontSize: 14 }}>
                Future Family provider portal
              </p>

              <form onSubmit={handleSubmit}>
                <div className="form-group email-box">
                  <input
                    type="email"
                    name="email"
                    placeholder="Enter E-Mail ID"
                    value={formData.email}
                    onChange={handleChange}
                    className={errors.email ? 'is-invalid' : ''}
                  />
                  {errors.email && (
                    <div className="invalid-feedback" style={{ display: 'block' }}>
                      {errors.email}
                    </div>
                  )}
                </div>

                <div className="form-group password-box">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    name="password"
                    placeholder="Enter Password"
                    value={formData.password}
                    onChange={handleChange}
                    className={errors.password ? 'is-invalid' : ''}
                  />
                  <span
                    className="toggle-eye"
                    onClick={togglePasswordVisibility}
                    style={{ cursor: 'pointer' }}
                  >
                    {showPassword ? '👁️' : '👁️‍🗨️'}
                  </span>
                  {errors.password && (
                    <div className="invalid-feedback" style={{ display: 'block' }}>
                      {errors.password}
                    </div>
                  )}
                </div>

                <button type="submit" disabled={isSubmitting}>
                  {isSubmitting ? 'LOGGING IN...' : 'LOGIN'}
                </button>

                <div className="helper-links">
                  Doctor accounts are created by an admin — contact support if you
                  don't have credentials yet.
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </section>
    <Footer/>
    </>
  );
};

export default DoctorLogin;